import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { css, styled } from 'styled-components';
import { Box } from '@design-system/box';
import { Container } from '@design-system/container';
import { useI18n } from '@design-system/i18n';
import { FakeLink } from '@design-system/link';
import { Simplebar } from '@design-system/simplebar';
import { TagsList } from '@design-system/tags-list';
const GRADIENT_WIDTH = 30;
const bgColor = `245, 246, 246`;
const bgColorMaskGradient = `var(--color-background, rgb(${bgColor})), var(--color-background-transparent, rgba(${bgColor}, 0))`;
const GradientLeft = styled.div `
  position: absolute;
  left: 0px;
  height: 100%;
  background: linear-gradient(to right, ${bgColorMaskGradient});
  width: ${({ $width }) => $width}px;
`;
const GradientRight = styled.div `
  position: absolute;
  right: 0px;
  height: 100%;
  background: linear-gradient(to left, ${bgColorMaskGradient});
  width: ${({ $width }) => $width}px;
`;
const SimpleBarWrapper = ({ children, showLeftGradient, showRightGradient, simpleBarRef, }) => (_jsxs(_Fragment, { children: [_jsx(Simplebar, { ref: simpleBarRef, style: {
                width: '100%',
                height: '44px',
            }, children: children }), showLeftGradient && _jsx(GradientLeft, { "$width": GRADIENT_WIDTH }), showRightGradient && _jsx(GradientRight, { "$width": GRADIENT_WIDTH })] }));
const NoWrapper = ({ children }) => children;
export const TagsFilters = ({ labelledBy, limit, multi, onClick, selectedTags = [], showAllTag, singleRow, tags, }) => {
    const { s__ } = useI18n();
    const scrollableNodeRef = useRef(null);
    const [tagsAreLimited, setTagsAreLimited] = useState(false);
    const [showLeftGradient, setShowLeftGradient] = useState(false);
    const [showRightGradient, setShowRightGradient] = useState(false);
    const stringNameTags = useMemo(() => tags === null || tags === void 0 ? void 0 : tags.map((_a) => {
        var { name } = _a, rest = __rest(_a, ["name"]);
        return (Object.assign({ name: `${name}` }, rest));
    }), [tags]);
    useEffect(() => {
        if ((stringNameTags === null || stringNameTags === void 0 ? void 0 : stringNameTags.length) > 0) {
            setTagsAreLimited(Boolean(limit) && limit !== undefined && stringNameTags.length > limit);
        }
    }, [limit, stringNameTags]);
    const updateGradientVisibility = () => {
        var _a, _b;
        const { offsetWidth, scrollLeft, scrollWidth } = (_b = (_a = scrollableNodeRef.current) === null || _a === void 0 ? void 0 : _a.contentWrapperEl) !== null && _b !== void 0 ? _b : {};
        setShowLeftGradient(scrollLeft > 0);
        setShowRightGradient(scrollLeft + offsetWidth < scrollWidth);
    };
    useEffect(() => {
        if (scrollableNodeRef.current) {
            scrollableNodeRef.current.contentWrapperEl.addEventListener('scroll', updateGradientVisibility);
            window.addEventListener('resize', updateGradientVisibility);
            updateGradientVisibility();
            const simpleBarWrapperEl = scrollableNodeRef.current.contentWrapperEl;
            return () => {
                simpleBarWrapperEl.removeEventListener('scroll', updateGradientVisibility);
                window.removeEventListener('resize', updateGradientVisibility);
            };
        }
        return undefined;
    }, []);
    const Wrapper = singleRow ? SimpleBarWrapper : NoWrapper;
    if (!(stringNameTags === null || stringNameTags === void 0 ? void 0 : stringNameTags.length)) {
        return null;
    }
    return (_jsx(Container, { css: css `
        justify-content: flex-start;
        position: relative;
      `, height: "auto", children: _jsx(Wrapper, { showLeftGradient: showLeftGradient, showRightGradient: showRightGradient, simpleBarRef: scrollableNodeRef, children: _jsxs(Box, { alignItems: "center", "aria-labelledby": labelledBy, "data-testid": "tags", flexWrap: singleRow ? 'nowrap' : 'wrap', mb: -3, role: "group", children: [_jsx(TagsList, { multi: multi, onChange: onClick, selectedTagNames: selectedTags, showAllTag: showAllTag, tags: tagsAreLimited && limit !== undefined ? stringNameTags.slice(0, limit) : stringNameTags }), Boolean(limit) && limit !== undefined && stringNameTags.length > limit && (_jsx(FakeLink, { flex: "0 0 auto", mb: 3, mr: 3, onClick: () => setTagsAreLimited((state) => !state), children: tagsAreLimited ? s__('Tags|See all') : s__('Tags|See less') }))] }) }) }));
};
//# sourceMappingURL=index.js.map